import { ASPECT_SIZES } from '../content-studio/video-editor/composition';
import { filmAspect, partLumaKey, textBox } from './film';
import { graphicsOf } from './graphics';
import { StudioBoard } from './studio-contract';

const SAMPLE_WIDTH = 96;
/** No picture may hold the film back: what is not read in time keeps the ordinary shadow. */
const LOAD_MS = 6000;

function withTimeout<T>(work: Promise<T>, ms: number): Promise<T> {
    return new Promise((resolve, reject) => {
        const timer = window.setTimeout(() => reject(new Error('timed out')), ms);
        work.then((v) => { window.clearTimeout(timer); resolve(v); }, (e) => { window.clearTimeout(timer); reject(e); });
    });
}

function loadImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error(`could not load ${url}`));
        img.src = url;
    });
}

/** A frame of a clip, a moment in, for shots that have no photo (3D tile clips). */
function loadVideoFrame(url: string, at = 0.5): Promise<HTMLVideoElement> {
    return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        video.crossOrigin = 'anonymous';
        video.muted = true;
        video.preload = 'auto';
        const fail = () => reject(new Error(`could not read ${url}`));
        video.onerror = fail;
        video.onloadedmetadata = () => { video.currentTime = Math.min(at, Math.max(0, (video.duration || 1) - 0.05)); };
        video.onseeked = () => resolve(video);
        window.setTimeout(fail, LOAD_MS);
        video.src = url;
    });
}

/** How bright a logo is, over its opaque pixels only (0 dark – 1 white). */
export async function measureLogo(url?: string): Promise<number | undefined> {
    if (!url) return undefined;
    try {
        const img = await withTimeout(loadImage(url), LOAD_MS);
        const w = SAMPLE_WIDTH;
        const h = Math.max(1, Math.round((SAMPLE_WIDTH * img.naturalHeight) / Math.max(1, img.naturalWidth)));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) return undefined;
        ctx.drawImage(img, 0, 0, w, h);
        const data = ctx.getImageData(0, 0, w, h).data;
        let sum = 0;
        let weight = 0;
        for (let i = 0; i < data.length; i += 4) {
            const a = data[i + 3] / 255;
            if (a < 0.1) continue;
            sum += a * (0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]) / 255;
            weight += a;
        }
        return weight ? sum / weight : undefined;
    } catch {
        return undefined;
    }
}

/**
 * How bright each shot's photo is under each of its text parts (0 dark – 1
 * white), so the film can deepen the shadow where light type sits on a light
 * picture. The photo is the clip's first frame, so it stands for the clip.
 * A photo that will not load (or taints the canvas) is skipped: those parts
 * keep the ordinary shadow.
 */
export async function measureLegibility(board: StudioBoard): Promise<Record<string, number>> {
    const aspect = filmAspect(board);
    const { width: W, height: H } = ASPECT_SIZES[aspect];
    const out: Record<string, number> = {};
    await Promise.all((board.shots || []).map(async (shot) => {
        const picture = shot.still?.url || shot.clip?.url;
        if (shot.inFilm === false || shot.kind === 'card' || !picture) return;
        const parts = graphicsOf(shot).filter((g) => g.type === 'text' && g.text.trim());
        if (!parts.length) return;
        try {
            const source: HTMLImageElement | HTMLVideoElement = shot.still?.url ? await withTimeout(loadImage(shot.still.url), LOAD_MS) : await loadVideoFrame(picture);
            const naturalWidth = source instanceof HTMLVideoElement ? source.videoWidth : source.naturalWidth;
            const naturalHeight = source instanceof HTMLVideoElement ? source.videoHeight : source.naturalHeight;
            const w = SAMPLE_WIDTH;
            const h = Math.max(1, Math.round((SAMPLE_WIDTH * H) / W));
            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            if (!ctx) return;
            // Cover, like the film: the photo fills the frame, centred.
            const scale = Math.max(w / naturalWidth, h / naturalHeight);
            const dw = naturalWidth * scale;
            const dh = naturalHeight * scale;
            ctx.drawImage(source, (w - dw) / 2, (h - dh) / 2, dw, dh);
            const data = ctx.getImageData(0, 0, w, h).data;
            for (const g of parts) {
                if (g.type !== 'text') continue;
                const box = textBox(g, aspect);
                const x0 = Math.max(0, Math.floor((box.x - box.width / 2) * w));
                const x1 = Math.min(w, Math.ceil((box.x + box.width / 2) * w));
                const y0 = Math.max(0, Math.floor((box.y - box.height / 2) * h));
                const y1 = Math.min(h, Math.ceil((box.y + box.height / 2) * h));
                let sum = 0;
                let n = 0;
                for (let y = y0; y < y1; y++) {
                    for (let x = x0; x < x1; x++) {
                        const i = (y * w + x) * 4;
                        sum += (0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]) / 255;
                        n++;
                    }
                }
                if (n) out[partLumaKey(shot.id, g)] = sum / n;
            }
        } catch {
            // Unreadable photo: its words keep the ordinary shadow.
        }
    }));
    return out;
}
